import { useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@packages/backend/convex/_generated/api";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle,
  Calendar,
  MessageSquare,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { PageHero } from "../components/PageHero";

export const Contact = () => {
  const createPublicTicket = useMutation(
    api.support.publicTickets.createPublicTicket,
  );

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    phone: "",
    service: "",
    teamSize: "",
    timeline: "",
    budget: "",
    message: "",
    preferredContact: "email",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const detailLines = [
        formData.phone && `Phone: ${formData.phone}`,
        formData.teamSize && `Team size: ${formData.teamSize}`,
        formData.timeline && `Timeline: ${formData.timeline}`,
        formData.budget && `Budget: ${formData.budget}`,
        `Preferred contact: ${formData.preferredContact}`,
      ]
        .filter(Boolean)
        .join("\n");

      await createPublicTicket({
        title: `${formData.service || "Partnership"} inquiry from ${formData.company}`,
        description: `${detailLines}\n\n${formData.message}`,
        category: "contact_form",
        priority: "medium",
        userEmail: formData.email,
        userDisplayName: formData.name,
        sourceApp: "bpo",
      });

      // Confirmation email to the customer is best-effort: the ticket
      // above is already the source of truth, so a Resend hiccup here
      // should not block the success state.
      try {
        const response = await fetch("/api/contact", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        });
        if (!response.ok) {
          const result = await response.json().catch(() => ({}));
          throw new Error(result.error || "Failed to send confirmation email");
        }
      } catch (emailError) {
        console.error("Failed to send confirmation email:", emailError);
      }

      setSubmitted(true);
    } catch (error) {
      console.error("Form submission error:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Failed to send your message. Please try again or contact us directly at support@kolaborate.co",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const services = [
    "Software Development & AI",
    "AI Data Services & Annotation",
    "Digital Media & Marketing",
    "Custom Enterprise Solution",
  ];

  const teamSizes = [
    "1-3 team members",
    "3-5 team members",
    "6-10 team members",
    "10+ team members",
  ];

  const timelines = [
    "ASAP (Rush project)",
    "2-4 weeks",
    "1-3 months",
    "3-6 months",
    "6+ months",
  ];

  const budgets = ["$5k - $15k", "$15k - $50k", "$50k - $100k", "$100k+"];

  const contactInfo = [
    {
      icon: Phone,

      title: "Phone Support",
      details: "+256 772 957 990",
      description: "Available Mon-Fri, 8AM-6PM EAT",
      action: "Call Now",
      href: "tel:+256772957990",
      gradient: "from-green-500 to-green-600",
    },
    {
      icon: Mail,
      title: "Email Support",
      details: "support@kolaborate.co",
      description: "We respond within 2 hours",
      action: "Send Email",
      href: "mailto:support@kolaborate.co",
      gradient: "from-green-500 to-green-600",
    },
    {
      icon: MessageSquare,
      title: "Live Chat",
      details: "Available 24/7",
      description: "Instant support for quick questions",
      action: "Start Chat",
      href: "#",
      gradient: "from-green-500 to-green-600",
    },
  ];

  const officeLocations = [
    {
      country: "Uganda",
      flag: "🇺🇬",
      city: "Kampala",
      address: "Plot 123, Kampala Road",
      phone: "+256 772 957 990",
      timezone: "EAT (UTC+3)",
    },
    {
      country: "Kenya",
      flag: "🇰🇪",
      city: "Nairobi",
      address: "Westlands Business District",
      phone: "+256 772 957 990",
      timezone: "EAT (UTC+3)",
    },
    {
      country: "Rwanda",
      flag: "🇷🇼",
      city: "Kigali",
      address: "Kigali Heights",
      phone: "+256 772 957 990",
      timezone: "CAT (UTC+2)",
    },
  ];

  const faqs = [
    {
      question: "How quickly can you start a project?",
      answer:
        "We can typically start new projects within 1-2 weeks. For urgent requirements, we offer expedited onboarding with additional resources.",
    },
    {
      question: "What's your pricing model?",
      answer:
        "We offer transparent hourly rates ranging from $12.74 to $17.03 per hour per person, depending on team size. All fees include salaries, benefits, training, and support.",
    },
    {
      question: "How do you ensure quality?",
      answer:
        "All our professionals go through rigorous multi-step assessments. We provide continuous training, performance monitoring, and dedicated account management.",
    },
    {
      question: "Can I scale my team up or down?",
      answer:
        "Absolutely! Our flexible model allows you to scale your team based on project requirements with just a few days' notice.",
    },
  ];

  if (submitted) {
    return (
      <div className="pt-20 lg:pt-32 min-h-screen flex items-center justify-center">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white border border-gray-200 rounded-3xl p-12 shadow-xl animate-fadeInUp">
            <div className="w-20 h-20 bg-gradient-to-r from-brand-primary to-brand-tertiary rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="text-white" size={40} />
            </div>
            <h2 className="text-3xl font-bold text-brand-grey mb-4">
              Welcome to the Partnership!
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              Thank you for choosing to partner with African excellence. We'll
              connect with you within 2 hours to begin discussing how we can
              honor your vision, create mutual value, and build a lasting
              relationship.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => {
                  setSubmitted(false);
                  setFormData({
                    name: "",
                    email: "",
                    company: "",
                    phone: "",
                    service: "",
                    teamSize: "",
                    timeline: "",
                    budget: "",
                    message: "",
                    preferredContact: "email",
                  });
                }}
                className="px-6 py-3 border-2 border-brand-primary text-brand-primary rounded-xl font-semibold hover:bg-brand-secondary transition-colors"
              >
                Start Another Partnership
              </button>
              <a
                href="tel:+256772957990"
                className="px-6 py-3 bg-gradient-to-r from-brand-primary to-brand-tertiary text-white rounded-xl font-semibold hover:shadow-lg transition-all duration-300"
              >
                Call Us Now
              </a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <PageHero
        title="Start Your African Excellence Journey"
        subtitle="Join global leaders who are harnessing Africa's exceptional talent. Let's build a partnership that honors talent, creates value, and transforms both your business and African communities."
      />

      {/* Quick Stats */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: "🌍", stat: "1,200+", label: "African Professionals" },
              { icon: "🤝", stat: "50+", label: "Global Partnerships" },
              { icon: "💰", stat: "UGX100m+", label: "Income Created" },
              { icon: "⭐", stat: "95%", label: "Client Satisfaction" },
            ].map((item, index) => (
              <div
                key={index}
                className="text-center bg-white border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="text-2xl mb-2">{item.icon}</div>
                <div className="text-xl font-bold text-brand-grey">
                  {item.stat}
                </div>
                <div className="text-sm text-gray-500">{item.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12 animate-fadeInUp">
            <h2 className="text-3xl font-bold text-brand-primary mb-4">
              Begin Your Partnership Journey
            </h2>
            <p className="text-lg text-gray-600">
              Choose how you'd like to start building relationships with African
              excellence
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactInfo.map((contact, index) => (
              <div
                key={index}
                className={`group bg-white border border-gray-200 rounded-3xl p-8 text-center shadow-lg hover:shadow-xl transition-all duration-500 transform hover:-translate-y-2 animate-fadeInUp`}
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div
                  className={`w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-r ${contact.gradient} flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300`}
                >
                  <contact.icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-brand-grey mb-3">
                  {contact.title}
                </h3>
                <p className="text-lg font-semibold text-gray-600 mb-2">
                  {contact.details}
                </p>
                <p className="text-sm text-gray-500 mb-6">
                  {contact.description}
                </p>
                <a
                  href={contact.href}
                  className={`inline-block px-6 py-3 bg-gradient-to-r ${contact.gradient} text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1`}
                >
                  {contact.action}
                </a>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Contact Form */}
            <div className="lg:col-span-3 animate-fadeInLeft">
              <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8 lg:p-12">
                <div className="mb-8">
                  <h2 className="text-3xl font-bold text-brand-grey mb-4">
                    Let's Build Something Amazing Together
                  </h2>
                  <p className="text-lg text-gray-600">
                    Share your vision with us. We'll match you with talented
                    African professionals who can turn your goals into reality
                    while creating positive impact.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-semibold text-brand-grey mb-2"
                      >
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formData.name}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white text-brand-grey placeholder-gray-400 rounded-xl focus:border-green-400 focus:outline-none transition-colors"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-semibold text-brand-grey mb-2"
                      >
                        Work Email *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formData.email}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white text-brand-grey placeholder-gray-400 rounded-xl focus:border-green-400 focus:outline-none transition-colors"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="company"
                        className="block text-sm font-semibold text-brand-grey mb-2"
                      >
                        Company Name *
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        required
                        value={formData.company}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white text-brand-grey placeholder-gray-400 rounded-xl focus:border-green-400 focus:outline-none transition-colors"
                        placeholder="Acme Corporation"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="phone"
                        className="block text-sm font-semibold text-brand-grey mb-2"
                      >
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white text-brand-grey placeholder-gray-400 rounded-xl focus:border-green-400 focus:outline-none transition-colors"
                        placeholder="+1 (555) 123-4567"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="service"
                        className="block text-sm font-semibold text-brand-grey mb-2"
                      >
                        Service Needed *
                      </label>
                      <select
                        id="service"
                        name="service"
                        required
                        value={formData.service}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white text-brand-grey placeholder-gray-400 rounded-xl focus:border-green-400 focus:outline-none transition-colors"
                      >
                        <option value="" className="bg-gray-800 text-white">
                          Select a service...
                        </option>
                        {services.map((service, index) => (
                          <option key={index} value={service}>
                            {service}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="teamSize"
                        className="block text-sm font-semibold text-brand-grey mb-2"
                      >
                        Team Size Needed
                      </label>
                      <select
                        id="teamSize"
                        name="teamSize"
                        value={formData.teamSize}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white text-brand-grey placeholder-gray-400 rounded-xl focus:border-green-400 focus:outline-none transition-colors"
                      >
                        <option value="" className="bg-gray-800 text-white">
                          Select team size...
                        </option>
                        {teamSizes.map((size, index) => (
                          <option key={index} value={size}>
                            {size}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="timeline"
                        className="block text-sm font-semibold text-brand-grey mb-2"
                      >
                        Project Timeline
                      </label>
                      <select
                        id="timeline"
                        name="timeline"
                        value={formData.timeline}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white text-brand-grey placeholder-gray-400 rounded-xl focus:border-green-400 focus:outline-none transition-colors"
                      >
                        <option value="" className="bg-gray-800 text-white">
                          Select timeline...
                        </option>
                        {timelines.map((timeline, index) => (
                          <option key={index} value={timeline}>
                            {timeline}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label
                        htmlFor="budget"
                        className="block text-sm font-semibold text-brand-grey mb-2"
                      >
                        Project Budget
                      </label>
                      <select
                        id="budget"
                        name="budget"
                        value={formData.budget}
                        onChange={handleInputChange}
                        className="w-full px-4 py-3 border-2 border-gray-200 bg-white text-brand-grey placeholder-gray-400 rounded-xl focus:border-green-400 focus:outline-none transition-colors"
                      >
                        <option value="" className="bg-gray-800 text-white">
                          Select budget range...
                        </option>
                        {budgets.map((budget, index) => (
                          <option key={index} value={budget}>
                            {budget}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-semibold text-brand-grey mb-2"
                    >
                      Your Vision & Partnership Goals *
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      rows={5}
                      value={formData.message}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border-2 border-gray-200 bg-white text-brand-grey placeholder-gray-400 rounded-xl focus:border-green-400 focus:outline-none transition-colors resize-vertical"
                      placeholder="Tell us about your project vision, the African talent skills you're seeking, and how you'd like to create positive impact together..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-white mb-3">
                      Preferred Contact Method
                    </label>
                    <div className="flex flex-wrap gap-4">
                      {[
                        { value: "email", label: "Email", icon: Mail },
                        { value: "phone", label: "Phone Call", icon: Phone },
                        { value: "video", label: "Video Call", icon: Calendar },
                      ].map(({ value, label, icon: Icon }) => (
                        <label key={value} className="flex items-center">
                          <input
                            type="radio"
                            name="preferredContact"
                            value={value}
                            checked={formData.preferredContact === value}
                            onChange={handleInputChange}
                            className="mr-2 text-green-600"
                          />
                          <Icon size={16} className="mr-1 text-gray-400" />
                          <span className="text-gray-300">{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-brand-primary to-brand-tertiary text-white font-bold py-4 px-8 rounded-xl hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {isSubmitting ? (
                      <>
                        <div className="loading-spinner w-5 h-5 border-2 border-white border-t-transparent rounded-full mr-3"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Begin Our Partnership
                        <Send className="ml-2" size={20} />
                      </>
                    )}
                  </button>

                  <p className="text-sm text-gray-400 text-center">
                    By submitting this form, you agree to our privacy policy.
                    We'll never share your information with third parties.
                  </p>
                </form>
              </div>
            </div>

            {/* Contact Info Sidebar */}
            <div className="lg:col-span-2 animate-fadeInRight">
              <div className="space-y-8">
                {/* Office Locations */}
                <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8">
                  <h3 className="text-2xl font-bold text-brand-grey mb-6 flex items-center">
                    <MapPin className="mr-3 text-brand-primary" size={24} />
                    Our Offices
                  </h3>
                  <div className="space-y-6">
                    {officeLocations.map((office, index) => (
                      <div
                        key={index}
                        className="border-l-4 border-brand-primary pl-4"
                      >
                        <div className="flex items-center mb-2">
                          <span className="text-2xl mr-2">{office.flag}</span>
                          <h4 className="font-bold text-brand-grey">
                            {office.country}
                          </h4>
                        </div>
                        <p className="text-gray-600 font-semibold">
                          {office.city}
                        </p>
                        <p className="text-sm text-gray-500">
                          {office.address}
                        </p>
                        <p className="text-sm text-gray-500">{office.phone}</p>
                        <p className="text-sm text-gray-400">
                          {office.timezone}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Business Hours */}
                <div className="bg-gradient-to-br from-brand-primary to-brand-tertiary rounded-3xl shadow-xl p-8 text-white">
                  <h3 className="text-2xl font-bold mb-6 flex items-center">
                    <Clock className="mr-3" size={24} />
                    Business Hours
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span>Monday - Friday:</span>
                      <span className="font-semibold">
                        8:00 AM - 6:00 PM EAT
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Saturday:</span>
                      <span className="font-semibold">
                        9:00 AM - 2:00 PM EAT
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>Sunday:</span>
                      <span className="font-semibold">
                        Emergency Support Only
                      </span>
                    </div>
                    <div className="border-t border-white/20 pt-3 mt-4">
                      <p className="text-green-100 text-sm">
                        24/7 emergency support available for active projects
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Answers Accordion Section */}
          <div className="mt-16 animate-fadeInUp">
            <div className="bg-white border border-gray-200 rounded-3xl shadow-xl p-8 lg:p-12 max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-brand-grey mb-8 text-center">
                Quick Answers
              </h2>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="border-2 border-gray-200 rounded-xl overflow-hidden transition-all duration-300 hover:border-brand-primary/50"
                  >
                    <button
                      onClick={() =>
                        setOpenFaqIndex(openFaqIndex === index ? null : index)
                      }
                      className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-brand-secondary/30 transition-colors"
                    >
                      <h3 className="font-semibold text-brand-grey pr-4">
                        {faq.question}
                      </h3>
                      <div className="flex-shrink-0 ml-2">
                        {openFaqIndex === index ? (
                          <ChevronUp className="text-brand-primary" size={20} />
                        ) : (
                          <ChevronDown className="text-gray-400" size={20} />
                        )}
                      </div>
                    </button>
                    <div
                      className={`overflow-hidden transition-all duration-300 ${
                        openFaqIndex === index ? "max-h-96" : "max-h-0"
                      }`}
                    >
                      <div className="px-6 pb-4 pt-2">
                        <p className="text-gray-600 leading-relaxed">
                          {faq.answer}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
